import React, {useState, useCallback, useMemo} from 'react';
import {View, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, ScrollView, Platform, Modal} from 'react-native';
import {Text, Snackbar, IconButton, Chip, Dialog, Portal, Button, TextInput} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import DateTimePicker from '@react-native-community/datetimepicker';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import {colors, spacing} from '../../theme';
import {paymentApi} from '../../services/api/paymentApi';
import {workerApi} from '../../services/api/workerApi';

const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE'];
const MODE_LABELS = {CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank Transfer', CHEQUE: 'Cheque'};

const SOURCE_OPTIONS = [
  {value: 'SIMPLE', label: 'Simple'},
  {value: 'BULK_PAYMENT', label: 'Bulk'},
];

const getMonthStart = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const formatDateISO = date => {
  if (!date) return null;
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const MODE_ICONS = {CASH: 'cash', UPI: 'cellphone-arrow-down', BANK_TRANSFER: 'bank-transfer', CHEQUE: 'checkbook'};

const PayoutCard = ({item}) => {
  const [expanded, setExpanded] = useState(false);
  const items = item.items || [];
  const hasItems = items.length > 0;

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric', year: 'numeric'});
  };

  const formatMode = mode => MODE_LABELS[mode] || mode;

  return (
    <View style={styles.card}>
      {/* Header: avatar + name + amount */}
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.cardHeader}
        onPress={() => hasItems && setExpanded(prev => !prev)}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>
            {item.payeeName?.charAt(0)?.toUpperCase() || '?'}
          </Text>
        </View>
        <View style={styles.cardBody}>
          <Text style={styles.workerName} numberOfLines={1}>{item.payeeName}</Text>
          <View style={styles.metaRow}>
            <View style={styles.modeTag}>
              <Icon name={MODE_ICONS[item.paymentMode] || 'cash'} size={12} color={colors.textSecondary} />
              <Text style={styles.modeText}>{formatMode(item.paymentMode)}</Text>
            </View>
            {item.sourceType === 'BULK_PAYMENT' && (
              <View style={styles.bulkTag}>
                <Text style={styles.bulkText}>Bulk</Text>
              </View>
            )}
            <View style={styles.dateTag}>
              <Icon name="calendar-outline" size={11} color={colors.textSecondary} />
              <Text style={styles.dateTagText}>{formatDate(item.paymentDate)}</Text>
            </View>
          </View>
        </View>
        <View style={styles.cardRight}>
          <Text style={styles.amount}>{'\u20B9'}{Number(item.totalAmount || 0).toLocaleString('en-IN')}</Text>
          <Text style={styles.txnCode}>{item.transactionCode}</Text>
        </View>
      </TouchableOpacity>

      {/* Footer: item count + expand hint */}
      <View style={styles.cardFooter}>
        {hasItems ? (
          <TouchableOpacity
            style={styles.expandHint}
            onPress={() => setExpanded(prev => !prev)}
            activeOpacity={0.7}>
            <Text style={styles.itemCountText}>
              {item.itemCount} item{item.itemCount !== 1 ? 's' : ''}
            </Text>
            <Icon
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={16}
              color={colors.textSecondary}
            />
          </TouchableOpacity>
        ) : (
          <Text style={styles.itemCountPlain}>{item.itemCount} item{item.itemCount !== 1 ? 's' : ''}</Text>
        )}
      </View>

      {/* Expanded items */}
      {expanded && hasItems && (
        <View style={styles.expandedSection}>
          {items.map((pi, idx) => {
            const isTask = pi.type === 'Task';
            return (
              <View key={pi.id || idx} style={[styles.expandedRow, idx === items.length - 1 && {borderBottomWidth: 0}]}>
                <View style={[styles.typeDot, {backgroundColor: isTask ? colors.primary : colors.warning}]} />
                <View style={{flex: 1}}>
                  <Text style={styles.expandedName} numberOfLines={1}>
                    {isTask
                      ? (pi.taskTypeName || 'Task')
                      : (pi.catalogItemName
                          ? `${pi.catalogItemName}${pi.instanceCode ? ' ' + pi.instanceCode : ''}`
                          : 'Dress Assignment')}
                  </Text>
                  {pi.orderNumber && (
                    <Text style={styles.expandedMeta}>#{pi.orderNumber}</Text>
                  )}
                </View>
                <Text style={styles.expandedAmount}>{'\u20B9'}{pi.amount}</Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

/* ── Main screen ─────────────────────────────────────────── */

const WorkerPayoutsScreen = () => {
  const {t} = useTranslation();
  const [payouts, setPayouts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});

  // Filter state — default: this month
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterWorkerId, setFilterWorkerId] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(getMonthStart);
  const [filterEndDate, setFilterEndDate] = useState(getToday);
  const [filterSourceType, setFilterSourceType] = useState(null);
  const [filterPaymentMode, setFilterPaymentMode] = useState(null);
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');

  // Temp filter state
  const [tmpWorkerId, setTmpWorkerId] = useState(null);
  const [tmpStartDate, setTmpStartDate] = useState(getMonthStart);
  const [tmpEndDate, setTmpEndDate] = useState(getToday);
  const [tmpSourceType, setTmpSourceType] = useState(null);
  const [tmpPaymentMode, setTmpPaymentMode] = useState(null);
  const [tmpMinAmount, setTmpMinAmount] = useState('');
  const [tmpMaxAmount, setTmpMaxAmount] = useState('');

  // Date picker
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [datePickerTarget, setDatePickerTarget] = useState(null);
  const [datePickerValue, setDatePickerValue] = useState(new Date());

  const hasFilters = filterWorkerId || filterStartDate || filterEndDate
    || filterSourceType || filterPaymentMode || filterMinAmount || filterMaxAmount;

  const workerMap = useMemo(() => {
    const map = {};
    workers.forEach(w => { map[w.id] = w.fullName; });
    return map;
  }, [workers]);

  const fetchPayouts = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const params = {};
      if (filterStartDate) params.from = formatDateISO(filterStartDate);
      if (filterEndDate) params.to = formatDateISO(filterEndDate);
      if (filterWorkerId) params.workerId = filterWorkerId;
      if (filterPaymentMode) params.paymentMode = filterPaymentMode;
      if (filterSourceType) params.sourceType = filterSourceType;
      if (filterMinAmount) params.minAmount = filterMinAmount;
      if (filterMaxAmount) params.maxAmount = filterMaxAmount;
      const res = await paymentApi.getHistory(params);
      setPayouts(res.data?.data || []);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to load payouts';
      setSnackbar({visible: true, message: msg});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterWorkerId, filterStartDate, filterEndDate, filterSourceType, filterPaymentMode, filterMinAmount, filterMaxAmount]);

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await workerApi.search({page: 0, size: 200, isActive: true});
      setWorkers(res.data?.data?.content || []);
    } catch (e) { /* silent */ }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchPayouts();
      fetchWorkers();
    }, [fetchPayouts, fetchWorkers]),
  );

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPayouts(false);
  };

  const openFilterModal = useCallback(() => {
    setTmpWorkerId(filterWorkerId);
    setTmpStartDate(filterStartDate);
    setTmpEndDate(filterEndDate);
    setTmpSourceType(filterSourceType);
    setTmpPaymentMode(filterPaymentMode);
    setTmpMinAmount(filterMinAmount);
    setTmpMaxAmount(filterMaxAmount);
    setFilterVisible(true);
  }, [filterWorkerId, filterStartDate, filterEndDate, filterSourceType, filterPaymentMode, filterMinAmount, filterMaxAmount]);

  const applyFilters = useCallback(() => {
    setFilterWorkerId(tmpWorkerId);
    setFilterStartDate(tmpStartDate);
    setFilterEndDate(tmpEndDate);
    setFilterSourceType(tmpSourceType);
    setFilterPaymentMode(tmpPaymentMode);
    setFilterMinAmount(tmpMinAmount);
    setFilterMaxAmount(tmpMaxAmount);
    setFilterVisible(false);
  }, [tmpWorkerId, tmpStartDate, tmpEndDate, tmpSourceType, tmpPaymentMode, tmpMinAmount, tmpMaxAmount]);

  const clearFilters = useCallback(() => {
    setFilterWorkerId(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterSourceType(null);
    setFilterPaymentMode(null);
    setFilterMinAmount('');
    setFilterMaxAmount('');
    setTmpWorkerId(null);
    setTmpStartDate(null);
    setTmpEndDate(null);
    setTmpSourceType(null);
    setTmpPaymentMode(null);
    setTmpMinAmount('');
    setTmpMaxAmount('');
    setFilterVisible(false);
  }, []);

  // Date picker helpers
  const openDatePicker = useCallback((target) => {
    const currentVal = target === 'start' ? tmpStartDate : tmpEndDate;
    setDatePickerTarget(target);
    setDatePickerValue(currentVal || new Date());
    setFilterVisible(false);
    setTimeout(() => setDatePickerVisible(true), 300);
  }, [tmpStartDate, tmpEndDate]);

  const handleDatePickerConfirm = useCallback(() => {
    if (datePickerTarget === 'start') setTmpStartDate(datePickerValue);
    else if (datePickerTarget === 'end') setTmpEndDate(datePickerValue);
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, [datePickerTarget, datePickerValue]);

  const handleDatePickerCancel = useCallback(() => {
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, []);

  const handleAndroidDateChange = useCallback((event, date) => {
    if (event.type === 'dismissed') {
      setDatePickerVisible(false);
      setTimeout(() => setFilterVisible(true), 300);
      return;
    }
    if (date) {
      if (datePickerTarget === 'start') setTmpStartDate(date);
      else if (datePickerTarget === 'end') setTmpEndDate(date);
    }
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, [datePickerTarget]);

  const formatDateShort = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const formatDateDisplay = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', {year: 'numeric', month: 'short', day: 'numeric'});
  };

  if (loading && payouts.length === 0) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Filter chips bar */}
      <View style={styles.chipBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipScroll}>
          {filterWorkerId && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterWorkerId(null)}>
              {workerMap[filterWorkerId] || 'Worker'}
            </Chip>
          )}
          {filterStartDate && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterStartDate(null)}>
              From: {formatDateShort(filterStartDate)}
            </Chip>
          )}
          {filterEndDate && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterEndDate(null)}>
              To: {formatDateShort(filterEndDate)}
            </Chip>
          )}
          {filterSourceType && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterSourceType(null)}>
              {filterSourceType === 'BULK_PAYMENT' ? 'Bulk' : 'Simple'}
            </Chip>
          )}
          {filterPaymentMode && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterPaymentMode(null)}>
              {MODE_LABELS[filterPaymentMode]}
            </Chip>
          )}
          {filterMinAmount ? (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterMinAmount('')}>
              Min: {'\u20B9'}{filterMinAmount}
            </Chip>
          ) : null}
          {filterMaxAmount ? (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterMaxAmount('')}>
              Max: {'\u20B9'}{filterMaxAmount}
            </Chip>
          ) : null}
          {hasFilters && (
            <TouchableOpacity onPress={clearFilters} style={styles.clearBtn}>
              <Text style={styles.clearText}>Clear</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
        <IconButton
          icon={hasFilters ? 'filter-check' : 'filter-variant'}
          size={22}
          iconColor={hasFilters ? colors.primary : colors.textSecondary}
          onPress={openFilterModal}
          style={styles.filterIconBtn}
        />
      </View>

      <FlatList
        data={payouts}
        keyExtractor={item => String(item.id)}
        renderItem={({item}) => <PayoutCard item={item} />}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        contentContainerStyle={styles.listContent}
        ListHeaderComponent={payouts.length > 0 ? (
          <View style={styles.legendRow}>
            <View style={[styles.typeDot, {backgroundColor: colors.primary}]} />
            <Text style={styles.legendText}>Task</Text>
            <View style={[styles.typeDot, {backgroundColor: colors.warning, marginLeft: spacing.md}]} />
            <Text style={styles.legendText}>Dress Assignment</Text>
          </View>
        ) : null}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Icon name="cash-multiple" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('worker.noPayouts')}</Text>
          </View>
        }
      />

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>

      {/* Filter Modal */}
      <Portal>
        <Dialog visible={filterVisible} onDismiss={() => setFilterVisible(false)} style={{borderRadius: 15}}>
          <View style={styles.dialogTitleRow}>
            <Text variant="titleLarge" style={{fontWeight: '600'}}>Filter Payouts</Text>
            <IconButton icon="close" size={22} onPress={() => setFilterVisible(false)} style={{margin: 0}} />
          </View>
          <Dialog.ScrollArea style={{maxHeight: 460, paddingHorizontal: 0}}>
            <ScrollView>
              <View style={styles.filterSection}>
                {/* Worker */}
                <Text variant="labelLarge" style={styles.filterLabel}>Worker</Text>
                <View style={styles.filterListBox}>
                  <ScrollView nestedScrollEnabled>
                    {workers.map(w => {
                      const selected = tmpWorkerId === w.id;
                      return (
                        <TouchableOpacity
                          key={w.id}
                          style={[styles.filterListItem, selected && styles.filterListItemSelected]}
                          onPress={() => setTmpWorkerId(selected ? null : w.id)}
                          activeOpacity={0.7}>
                          <Text style={[styles.filterListItemText, selected && styles.filterListItemTextSelected]}>
                            {w.fullName}
                          </Text>
                          {selected && <IconButton icon="check" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />}
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                {/* Date range */}
                <Text variant="labelLarge" style={styles.filterLabel}>Payment Date</Text>
                <View style={styles.dateRow}>
                  <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker('start')}>
                    <IconButton icon="calendar" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    <Text style={tmpStartDate ? styles.dateText : styles.datePlaceholder}>
                      {tmpStartDate ? formatDateDisplay(tmpStartDate) : 'Start date'}
                    </Text>
                    {tmpStartDate && (
                      <IconButton icon="close" size={14} onPress={(e) => { e?.stopPropagation?.(); setTmpStartDate(null); }} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    )}
                  </TouchableOpacity>
                  <Text style={styles.dateSep}>to</Text>
                  <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker('end')}>
                    <IconButton icon="calendar" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    <Text style={tmpEndDate ? styles.dateText : styles.datePlaceholder}>
                      {tmpEndDate ? formatDateDisplay(tmpEndDate) : 'End date'}
                    </Text>
                    {tmpEndDate && (
                      <IconButton icon="close" size={14} onPress={(e) => { e?.stopPropagation?.(); setTmpEndDate(null); }} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    )}
                  </TouchableOpacity>
                </View>

                {/* Source type */}
                <Text variant="labelLarge" style={styles.filterLabel}>Payment Type</Text>
                <View style={styles.filterChipGroup}>
                  {SOURCE_OPTIONS.map(s => (
                    <Chip key={s.value} selected={tmpSourceType === s.value}
                      onPress={() => setTmpSourceType(tmpSourceType === s.value ? null : s.value)}
                      style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                      {s.label}
                    </Chip>
                  ))}
                </View>

                {/* Payment mode */}
                <Text variant="labelLarge" style={styles.filterLabel}>Payment Mode</Text>
                <View style={styles.filterChipGroup}>
                  {PAYMENT_MODES.map(m => (
                    <Chip key={m} selected={tmpPaymentMode === m}
                      onPress={() => setTmpPaymentMode(tmpPaymentMode === m ? null : m)}
                      style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                      {MODE_LABELS[m]}
                    </Chip>
                  ))}
                </View>

                {/* Amount range */}
                <Text variant="labelLarge" style={styles.filterLabel}>Amount Range</Text>
                <View style={styles.amountRow}>
                  <TextInput
                    mode="outlined"
                    placeholder="Min"
                    value={tmpMinAmount}
                    onChangeText={setTmpMinAmount}
                    keyboardType="numeric"
                    dense
                    style={styles.amountInput}
                    left={<TextInput.Affix text={'\u20B9'} />}
                  />
                  <Text style={styles.dateSep}>to</Text>
                  <TextInput
                    mode="outlined"
                    placeholder="Max"
                    value={tmpMaxAmount}
                    onChangeText={setTmpMaxAmount}
                    keyboardType="numeric"
                    dense
                    style={styles.amountInput}
                    left={<TextInput.Affix text={'\u20B9'} />}
                  />
                </View>
              </View>
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={clearFilters}>Clear Filters</Button>
            <Button mode="contained" onPress={applyFilters}
              style={{borderRadius: 10}} buttonColor={colors.primary}>
              Apply Filters
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Date picker */}
      {Platform.OS === 'ios' ? (
        <Modal visible={datePickerVisible} transparent animationType="slide">
          <View style={styles.datePickerOverlay}>
            <View style={styles.datePickerSheet}>
              <View style={styles.datePickerHeader}>
                <Button onPress={handleDatePickerCancel}>Cancel</Button>
                <Text variant="titleMedium" style={{fontWeight: '600'}}>
                  {datePickerTarget === 'start' ? 'Start Date' : 'End Date'}
                </Text>
                <Button onPress={handleDatePickerConfirm}>Done</Button>
              </View>
              <DateTimePicker
                value={datePickerValue}
                mode="date"
                display="spinner"
                onChange={(event, date) => { if (date) setDatePickerValue(date); }}
                style={{height: 200}}
              />
            </View>
          </View>
        </Modal>
      ) : (
        datePickerVisible && (
          <DateTimePicker
            value={datePickerValue}
            mode="date"
            display="default"
            onChange={handleAndroidDateChange}
          />
        )
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  centerContent: {justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.sm, paddingBottom: spacing.xl, gap: spacing.sm},

  // Chip bar
  chipBar: {
    flexDirection: 'row', alignItems: 'center',
    paddingLeft: spacing.sm, backgroundColor: colors.surface,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.divider,
  },
  chipScroll: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.xs, gap: spacing.xs,
  },
  filterChip: {backgroundColor: colors.primary + '20', height: 30},
  filterChipText: {fontSize: 12},
  clearBtn: {justifyContent: 'center', paddingHorizontal: spacing.xs},
  clearText: {color: colors.primary, fontSize: 12, fontWeight: '600'},
  filterIconBtn: {margin: 0},

  // Payout card
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    gap: spacing.sm,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {fontSize: 16, fontWeight: '700', color: colors.primary},
  cardBody: {flex: 1},
  workerName: {fontSize: 15, fontWeight: '600', color: colors.text},
  metaRow: {flexDirection: 'row', flexWrap: 'wrap', marginTop: 3, gap: 6},
  modeTag: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    backgroundColor: colors.background, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
  },
  modeText: {fontSize: 11, color: colors.textSecondary, fontWeight: '500'},
  bulkTag: {
    backgroundColor: '#FF980015', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
  },
  bulkText: {fontSize: 11, color: '#FF9800', fontWeight: '600'},
  dateTag: {flexDirection: 'row', alignItems: 'center', gap: 3},
  dateTagText: {fontSize: 11, color: colors.textSecondary},
  cardRight: {alignItems: 'flex-end'},
  amount: {fontSize: 17, fontWeight: '800', color: colors.primary},
  txnCode: {fontSize: 10, color: colors.textSecondary, marginTop: 2},

  // Card footer
  cardFooter: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    marginLeft: 48,
  },
  expandHint: {flexDirection: 'row', alignItems: 'center', gap: 2},
  itemCountText: {fontSize: 12, color: colors.textSecondary, fontWeight: '500'},
  itemCountPlain: {fontSize: 12, color: colors.textSecondary},

  // Expanded items
  expandedSection: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.divider,
  },
  expandedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingLeft: spacing.md + 48,
    paddingRight: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
    gap: spacing.sm,
  },
  typeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  expandedName: {fontSize: 13, fontWeight: '500', color: colors.text},
  expandedMeta: {fontSize: 11, color: colors.textSecondary, marginTop: 1},
  expandedAmount: {fontSize: 14, fontWeight: '700', color: colors.text},
  legendRow: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: spacing.sm,
  },
  legendText: {fontSize: 11, color: colors.textSecondary},

  // Empty
  emptyContainer: {
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },
  // Filter modal styles
  dialogTitleRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.lg, paddingTop: spacing.md, paddingBottom: spacing.xs,
  },
  filterSection: {paddingHorizontal: spacing.lg, paddingVertical: spacing.sm},
  filterLabel: {fontWeight: '600', color: colors.text, marginTop: spacing.md, marginBottom: spacing.xs},
  filterChipGroup: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  filterSelectChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  filterListBox: {
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, maxHeight: 100, overflow: 'hidden',
  },
  filterListItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 5, paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.divider,
  },
  filterListItemSelected: {backgroundColor: colors.primary + '12'},
  filterListItemText: {fontSize: 14, color: colors.text},
  filterListItemTextSelected: {fontWeight: '600', color: colors.primary},
  dateRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  dateInput: {
    flex: 1, flexDirection: 'row', alignItems: 'center', gap: 4,
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, paddingHorizontal: 6, paddingVertical: 8,
  },
  dateText: {fontSize: 12, color: colors.text, flex: 1},
  datePlaceholder: {fontSize: 12, color: colors.textSecondary, flex: 1},
  dateSep: {fontSize: 13, color: colors.textSecondary},
  amountRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  amountInput: {flex: 1, backgroundColor: colors.surface, fontSize: 14},
  // Date picker modal (iOS)
  datePickerOverlay: {
    flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.4)',
  },
  datePickerSheet: {
    backgroundColor: colors.surface, borderTopLeftRadius: 16, borderTopRightRadius: 16,
    paddingBottom: 30,
  },
  datePickerHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
});

export default WorkerPayoutsScreen;
